import { differenceInDays, format, parseISO, subDays, subMonths, subYears } from 'date-fns';
import { getFirstDayOfMonth, getLastDayOfMonth } from '@/utils/formatDates';

const dateFormat = 'yyyy-MM-dd';

// Период сравнения: [начало, конец] в формате yyyy-MM-dd.
export type ComparePeriod = [string, string];

// Зазор по умолчанию: сравнение примыкает к основному периоду слева.
const DEFAULT_GAP_DAYS = 1;

const toDate = (value: string): Date => parseISO(value);
const toText = (value: Date): string => format(value, dateFormat);

// Длина периода в днях: 01.09-01.09 это 1 день, 01.09-18.09 - 18.
const getLengthInDays = (from: string, to: string): number =>
    Math.abs(differenceInDays(toDate(to), toDate(from))) + 1;

// Основной период занимает календарный месяц целиком: пресет "прошлый месяц",
// а также "текущий месяц", открытый в последний день месяца.
export const isFullCalendarMonth = (from: string, to: string): boolean =>
    !!from && !!to &&
    toText(getFirstDayOfMonth(from)) === from &&
    toText(getLastDayOfMonth(from)) === to;

// Равное окно слева от основного периода. gapDays - расстояние от конца сравнения
// до начала основного периода, 1 значит вплотную.
export const getComparePeriodByGap = (
    from: string,
    to: string,
    gapDays: number = DEFAULT_GAP_DAYS,
): ComparePeriod => {
    const compareTo = subDays(toDate(from), gapDays);
    const compareFrom = subDays(compareTo, getLengthInDays(from, to) - 1);

    return [toText(compareFrom), toText(compareTo)];
};

// Полный календарный месяц сравнивается с полным месяцем на monthsOffset назад.
// Сдвигать оба конца на дни здесь нельзя: длина месяцев разная и конец уезжает.
export const getComparePeriodByMonths = (from: string, monthsOffset: number = 1): ComparePeriod => {
    const base = subMonths(toDate(from), monthsOffset);

    return [toText(getFirstDayOfMonth(base)), toText(getLastDayOfMonth(base))];
};

// Период сравнения от выбранного пользователем начала: длина всегда равна основному
// периоду, наложение на основной период схлопывается до примыкания.
export const getComparePeriodFromStart = (from: string, to: string, compareFrom: string): ComparePeriod => {
    const compareTo = subDays(toDate(compareFrom), 1 - getLengthInDays(from, to));

    if (compareTo >= toDate(from)) {
        return getComparePeriodByGap(from, to);
    }

    return [compareFrom, toText(compareTo)];
};

// То, что датапикер подставляет сам при выборе основного периода. Эталон для
// сохранения и разворота отчёта: сохранённый отчёт обязан открываться так же.
export const getDefaultComparePeriod = (from: string, to: string): ComparePeriod =>
    isFullCalendarMonth(from, to)
        ? getComparePeriodByMonths(from)
        : getComparePeriodByGap(from, to);

// Зазор между концом сравнения и началом основного периода, 1 значит вплотную.
export const getCompareGapInDays = (mainFrom: string, compareTo: string): number =>
    differenceInDays(toDate(mainFrom), toDate(compareTo));

export const getCompareOffsetInMonths = (mainFrom: string, compareFrom: string): number => {
    const main = toDate(mainFrom);
    const compare = toDate(compareFrom);

    return (main.getFullYear() * 12 + main.getMonth()) - (compare.getFullYear() * 12 + compare.getMonth());
};

// Единица, в которой сохранённый отчёт помнит сдвиг сравнения.
export type CompareOffsetType = 'offset_days' | 'offset_months' | 'offset_years';

export interface ICompareOffset {
    type: CompareOffsetType;
    value: number;
}

// Начало основного периода, отодвинутое назад на offset единиц.
const shiftBack = (from: string, type: CompareOffsetType, offset: number): string => {
    if (type === 'offset_months') {
        return toText(subMonths(toDate(from), offset));
    }
    if (type === 'offset_years') {
        return toText(subYears(toDate(from), offset));
    }

    return toText(subDays(toDate(from), offset));
};

const getOffsetIn = (type: CompareOffsetType, mainFrom: string, compareFrom: string): number => {
    if (type === 'offset_months') {
        return getCompareOffsetInMonths(mainFrom, compareFrom);
    }
    if (type === 'offset_years') {
        return toDate(mainFrom).getFullYear() - toDate(compareFrom).getFullYear();
    }

    return differenceInDays(toDate(mainFrom), toDate(compareFrom));
};

// Сдвиг начала сравнения от начала основного периода в единицах type. Начало, которое
// не ложится ровно на N месяцев или лет назад (текущий месяц против 10.08), уходит
// в днях: иначе в тот же день отчёт откроется не с тем сравнением, что сохраняли.
export const getCompareOffset = (mainFrom: string, compareFrom: string, type: CompareOffsetType): ICompareOffset => {
    const value = getOffsetIn(type, mainFrom, compareFrom);

    return shiftBack(mainFrom, type, value) === compareFrom
        ? { type, value }
        : { type: 'offset_days', value: getOffsetIn('offset_days', mainFrom, compareFrom) };
};

// Период сравнения сохранённого отчёта на день открытия. Начало отодвигается от начала
// основного периода на сдвиг, длина равна основному периоду, как при выборе начала в
// датапикере. Наложение на основной период схлопывается до примыкания, поэтому сдвиг
// в 1 день всегда значит "вплотную", как бы ни вырос основной период.
export const getComparePeriodByOffset = (from: string, to: string, offset: ICompareOffset): ComparePeriod => {
    const compareFrom = shiftBack(from, offset.type, offset.value);

    // Полный месяц датапикер сравнивает только с полным месяцем.
    if (isFullCalendarMonth(from, to)) {
        return getComparePeriodByMonths(from, Math.max(1, getCompareOffsetInMonths(from, compareFrom)));
    }

    return getComparePeriodFromStart(from, to, compareFrom);
};
